import { ChevronDown, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import { formatDateTime } from '@/lib/utils';
import type { TransmitterV2Document } from '../../types';

// =============================================================================
// Props
// =============================================================================

interface DiagnosticsTabProps {
  transmitter: TransmitterV2Document;
  onRefresh: () => void;
}

// =============================================================================
// OTA type label map
// =============================================================================

const otaTypeLabels: Record<string, string> = {
  bulk: 'Bulk',
  FOTA: 'Firmware OTA',
  COTA: 'Config OTA',
  both: 'Both',
};

// =============================================================================
// Helpers
// =============================================================================

function safeFormatDateTime(value: string | undefined | null): string {
  if (!value) return '--';
  try {
    return formatDateTime(value);
  } catch {
    return value;
  }
}

// =============================================================================
// Collapsible Panel wrapper
// =============================================================================

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Collapsible className="border rounded-lg">
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50">
        <span className="font-medium text-sm">{title}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </CollapsibleTrigger>
      <CollapsibleContent className="p-3 pt-0">{children}</CollapsibleContent>
    </Collapsible>
  );
}

// =============================================================================
// Panel: Current Gateways
// =============================================================================

function CurrentGatewaysPanel({
  gateways,
}: {
  gateways: TransmitterV2Document['current_parent_gateways'];
}) {
  if (!gateways || gateways.length === 0) {
    return (
      <Panel title="Current Gateways">
        <p className="text-muted-foreground text-sm">No gateways</p>
      </Panel>
    );
  }

  return (
    <Panel title="Current Gateways">
      <div className="space-y-2">
        {gateways.map((gw, idx) => (
          <div
            key={idx}
            className="grid grid-cols-3 gap-2 text-sm border-b last:border-b-0 pb-2 last:pb-0"
          >
            <div>
              <span className="text-muted-foreground text-xs">MAC</span>
              <p className="font-mono text-xs">{gw.mac}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">
                Last Packet At
              </span>
              <p className="text-xs">{safeFormatDateTime(gw.time)}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">RSSI</span>
              <p className="text-xs">{gw.rssi} dB</p>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// =============================================================================
// Panel: History Gateways
// =============================================================================

function HistoryGatewaysPanel({
  history,
}: {
  history: TransmitterV2Document['current_parent_gateways_history'];
}) {
  if (!history || history.length === 0) {
    return (
      <Panel title="History Gateways">
        <p className="text-muted-foreground text-sm">No gateway history</p>
      </Panel>
    );
  }

  return (
    <Panel title="History Gateways">
      <div className="space-y-2">
        {history.map((gw, idx) => (
          <div
            key={idx}
            className="grid grid-cols-2 gap-2 text-sm border-b last:border-b-0 pb-2 last:pb-0"
          >
            <div>
              <span className="text-muted-foreground text-xs">MAC</span>
              <p className="font-mono text-xs">{gw.mac}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">
                Last Packet At
              </span>
              <p className="text-xs">{safeFormatDateTime(gw.last_ts)}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">
                First Packet At
              </span>
              <p className="text-xs">{safeFormatDateTime(gw.first_ts)}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Max RSSI</span>
              <p className="text-xs">
                {gw.max_rssi} dB at {safeFormatDateTime(gw.max_rssi_ts)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// =============================================================================
// Panel: Config JSON History
// =============================================================================

function ConfigJsonHistoryPanel({
  history,
}: {
  history: TransmitterV2Document['configjson_history'];
}) {
  if (!history || history.length === 0) {
    return (
      <Panel title="Config JSON History">
        <p className="text-muted-foreground text-sm">No config history</p>
      </Panel>
    );
  }

  return (
    <Panel title="Config JSON History">
      <div className="space-y-3">
        {history.map((entry, idx) => (
          <div key={idx} className="text-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-muted-foreground text-xs">
                {safeFormatDateTime(entry.date)}
              </span>
              {entry.inspec ? (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                  In Spec
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                  Out of Spec
                </span>
              )}
            </div>
            <pre className="bg-muted p-2 text-xs rounded overflow-x-auto whitespace-pre-wrap">
              {entry.json}
            </pre>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// =============================================================================
// Panel: OTA Log
// =============================================================================

function OtaLogPanel({ log }: { log: TransmitterV2Document['ota_log'] }) {
  if (!log || log.length === 0) {
    return (
      <Panel title="OTA Log">
        <p className="text-muted-foreground text-sm">No OTA logs</p>
      </Panel>
    );
  }

  return (
    <Panel title="OTA Log">
      <div className="space-y-2">
        {log.map((entry, idx) => (
          <div
            key={idx}
            className="flex items-center gap-4 text-sm border-b last:border-b-0 pb-2 last:pb-0"
          >
            <span className="text-muted-foreground text-xs min-w-[140px]">
              {safeFormatDateTime(entry.date)}
            </span>
            <span className="text-xs">{entry.user}</span>
            <span className="text-xs font-medium">
              {otaTypeLabels[entry.type] || entry.type}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// =============================================================================
// DiagnosticsTab
// =============================================================================

export function DiagnosticsTab({
  transmitter,
  onRefresh,
}: DiagnosticsTabProps) {
  return (
    <div className="space-y-3 py-2">
      <div className="flex justify-end gap-2">
        {transmitter.dev_id && (
          <Button asChild variant="outline" size="sm">
            <a
              href={`/tools/transmitter-raw-events?search=${encodeURIComponent(transmitter.dev_id)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Raw Archive
              <ExternalLink className="h-3.5 w-3.5 ml-1" aria-hidden="true" />
            </a>
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      <CurrentGatewaysPanel gateways={transmitter.current_parent_gateways} />
      <HistoryGatewaysPanel
        history={transmitter.current_parent_gateways_history}
      />
      <ConfigJsonHistoryPanel history={transmitter.configjson_history} />
      <OtaLogPanel log={transmitter.ota_log} />
    </div>
  );
}
